import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import classnames from "classnames";
import Icon from "components/components/Icon/Icon";
import { IconSize } from "components/components/Icon/IconWrapper";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  DropdownContainer,
  DropdownIconWrapper,
  DropdownNavItem,
  DropdownTitle,
  StyledLink,
  StyledNavButton,
} from "./style";

interface Props {
  linkText: string;
  isActive: boolean;
  linkTo?: string;
  dropdownTitleOnClick?: () => void;
  children?: React.ReactNode;
  onClick?: () => void;
  onMouseLeave?: (navButtonId: string) => void;
  isDropdownOpen?: boolean;
  external?: boolean;
}

const NavLink: React.FC<Props> = ({
  linkText,
  linkTo,
  dropdownTitleOnClick,
  isActive,
  children,
  onClick = (): void => {},
  onMouseLeave = (navButtonId): void => {
    document.getElementById(navButtonId)?.blur();
  },
  isDropdownOpen,
  external,
}) => {
  const navigate = useNavigate();

  const navigateTo = (link: string, callback?: () => void): void => {
    if (external) {
      window.open(link);
      return;
    }

    navigate(link);

    if (callback) {
      callback();
    }
  };

  const onDropdownTitleClick = (
    event: React.MouseEvent<HTMLAnchorElement>
  ): void => {
    if (!linkTo) {
      return;
    }

    event.preventDefault();

    navigateTo(linkTo, dropdownTitleOnClick);
  };

  const linkTextReplace = linkText.replace(/\s/g, "-").toLowerCase();
  const navLinkTestIdSpaceless = `nav-link-${linkTextReplace}`;
  const navButtonIdSpaceless = `nav-button-${linkTextReplace}`;
  const dropdownId = `nav-dropdown-${linkTextReplace}`;

  useEffect(() => {
    if (!isDropdownOpen) {
      document.getElementById(navButtonIdSpaceless)?.blur();
    }
  }, [navButtonIdSpaceless, isDropdownOpen]);

  if (children) {
    return (
      <DropdownNavItem
        className={classnames("navbar-item has-dropdown", {
          "is-open": isDropdownOpen,
        })}
        onMouseLeave={(): void => onMouseLeave(navButtonIdSpaceless)}
      >
        <StyledLink
          className="navbar-link is-arrowless"
          data-test-id={navLinkTestIdSpaceless}
          as="div"
        >
          <StyledNavButton
            type="button"
            id={navButtonIdSpaceless}
            className={classnames({ active: isActive })}
            aria-expanded={isDropdownOpen}
            aria-controls={dropdownId}
            onClick={onClick}
          >
            {linkText}
            <DropdownIconWrapper>
              <Icon
                iconName={faChevronDown}
                colour="white"
                size={IconSize.small}
              />
            </DropdownIconWrapper>
          </StyledNavButton>
        </StyledLink>

        <DropdownContainer className="navbar-dropdown" id={dropdownId}>
          <DropdownTitle>
            {linkTo ? (
              <a href={linkTo} onClick={onDropdownTitleClick}>
                {linkText}
              </a>
            ) : (
              linkText
            )}
          </DropdownTitle>
          {children}
        </DropdownContainer>
      </DropdownNavItem>
    );
  }

  return linkTo ? (
    <StyledLink
      className="navbar-item"
      onClick={(): void => {
        navigateTo(linkTo, onClick);
      }}
      data-test-id={navLinkTestIdSpaceless}
    >
      <StyledNavButton
        type="button"
        id={navButtonIdSpaceless}
        className={classnames({ active: isActive })}
      >
        {linkText}
      </StyledNavButton>
    </StyledLink>
  ) : null;
};

export default NavLink;
